import { useQuery } from '@apollo/client'
import { Tab, Tabs } from '@mui/material'
import { ProcessStatusGroup } from '__generated__/graphql'
import { GetRosterCandidateStatusGroupCounts } from 'client/graphql/search.gql'
import { useEffect } from 'react'
import { useCandidateSqlSearch } from 'recruiter/hooks'

const STATUS_GROUPS = [
    ProcessStatusGroup.Submitted,
    ProcessStatusGroup.InProcess,
    ProcessStatusGroup.OutOfProcess,
] as const

const TAB_NAMES: Record<(typeof STATUS_GROUPS)[number], string> = {
    [ProcessStatusGroup.Submitted]: 'Submitted',
    [ProcessStatusGroup.InProcess]: 'In Process',
    [ProcessStatusGroup.OutOfProcess]: 'Out of Process',
}

export function ProcessStatusGroupTabs() {
    const { tableState, setTableState, rosterId, totalItems } =
        useCandidateSqlSearch()

    const activeGroup =
        tableState.filters.processStatusGroup ?? ProcessStatusGroup.Submitted

    useEffect(() => {
        if (!tableState.filters.processStatusGroup) {
            setTableState({
                filters: {
                    ...tableState.filters,
                    processStatusGroup: ProcessStatusGroup.Submitted,
                },
                page: 1,
            })
        }
    }, [tableState.filters.processStatusGroup])

    const { data: countsData } = useQuery(GetRosterCandidateStatusGroupCounts, {
        variables: {
            rosterId: rosterId!,
            summaryGroups: [...STATUS_GROUPS],
        },
        skip: !rosterId,
    })

    const groupCounts = countsData?.getRosterCandidateStatusGroupCounts ?? []

    const getLabel = (group: ProcessStatusGroup) => {
        const name = TAB_NAMES[group]
        const total = groupCounts.find(
            (g) => g.statusSummaryGroup.summaryGroup === group
        )?.count
        if (total === undefined) return name
        if (group === activeGroup) return `${name} (${totalItems} of ${total})`
        return `${name} (${total})`
    }

    const handleChange = (
        _: React.SyntheticEvent,
        value: ProcessStatusGroup
    ) => {
        setTableState({
            filters: { ...tableState.filters, processStatusGroup: value },
            page: 1,
        })
    }

    return (
        <Tabs
            value={activeGroup}
            onChange={handleChange}
            sx={{
                minHeight: 0,
                '& .MuiTab-root': {
                    minHeight: 0,
                    py: 0.5,
                    px: 1.5,
                    fontSize: '0.75rem',
                    textTransform: 'none',
                },
                '& .MuiTabs-indicator': {
                    height: 2,
                },
            }}
        >
            {STATUS_GROUPS.map((group) => (
                <Tab key={group} label={getLabel(group)} value={group} />
            ))}
        </Tabs>
    )
}
